import React, { useState, useRef } from 'react';
import { Upload, FileText, Calculator, Atom, Info } from 'lucide-react';
import './App.css';

function App() {
  const [inputType, setInputType] = useState('sequence'); // 'sequence' or 'pdb'
  const [proteinSequence, setProteinSequence] = useState('');
  const [pdbFile, setPdbFile] = useState(null);
  const [atomCount, setAtomCount] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Function to count atoms from protein sequence
  const countAtomsFromSequence = (sequence) => {
    // Remove whitespace and convert to uppercase
    const cleanSequence = sequence.replace(/\s+/g, '').toUpperCase();
    
    // Validate sequence contains only amino acids
    const validAminoAcids = 'ACDEFGHIKLMNPQRSTVWY';
    if (!/^[ACDEFGHIKLMNPQRSTVWY]+$/.test(cleanSequence)) {
      throw new Error('Invalid protein sequence. Only standard amino acid codes are allowed.');
    }

    // Average atom counts per amino acid (approximate)
    const atomCountsPerResidue = {
      'A': 10, // Alanine
      'C': 12, // Cysteine
      'D': 13, // Aspartic acid
      'E': 14, // Glutamic acid
      'F': 20, // Phenylalanine
      'G': 7,  // Glycine
      'H': 16, // Histidine
      'I': 19, // Isoleucine
      'K': 18, // Lysine
      'L': 19, // Leucine
      'M': 16, // Methionine
      'N': 14, // Asparagine
      'P': 12, // Proline
      'Q': 15, // Glutamine
      'R': 21, // Arginine
      'S': 11, // Serine
      'T': 13, // Threonine
      'V': 17, // Valine
      'W': 25, // Tryptophan
      'Y': 21  // Tyrosine
    };

    let totalAtoms = 0;
    for (let i = 0; i < cleanSequence.length; i++) {
      totalAtoms += atomCountsPerResidue[cleanSequence[i]] || 0;
    }

    return {
      totalAtoms,
      residueCount: cleanSequence.length,
      breakdown: cleanSequence.split('').reduce((acc, aa) => {
        acc[aa] = (acc[aa] || 0) + 1;
        return acc;
      }, {})
    };
  };

  // Function to count atoms from PDB file
  const countAtomsFromPDB = (pdbContent) => {
    const lines = pdbContent.split('\n');
    let atomCount = 0;
    let residueCount = 0;
    const residues = new Set();

    for (const line of lines) {
      if (line.startsWith('ATOM  ') || line.startsWith('HETATM')) {
        atomCount++;
        
        // Extract residue identifier (residue number + chain)
        const residueNum = line.substring(22, 26).trim();
        const chainId = line.substring(21, 22);
        const residueKey = `${chainId}_${residueNum}`;
        
        if (!residues.has(residueKey)) {
          residues.add(residueKey);
          residueCount++;
        }
      }
    }

    if (atomCount === 0) {
      throw new Error('No atoms found in PDB file. Please check the file format.');
    }

    return {
      totalAtoms: atomCount,
      residueCount,
      breakdown: null
    };
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidPdbFile = fileName.endsWith('.pdb') && (
        file.type === 'text/plain' || 
        file.type === 'application/octet-stream' || 
        file.type === '' || 
        file.type.startsWith('text/')
      );
      
      if (isValidPdbFile) {
        setPdbFile(file);
        setError('');
      } else {
        setError('Please upload a valid PDB file (.pdb extension)');
      }
    } else {
      setError('No file selected');
    }
  };

  const calculateAtoms = async () => {
    setIsCalculating(true);
    setError('');
    setAtomCount(null);

    try {
      let result;
      
      if (inputType === 'sequence') {
        if (!proteinSequence.trim()) {
          throw new Error('Please enter a protein sequence');
        }
        result = countAtomsFromSequence(proteinSequence);
      } else {
        if (!pdbFile) {
          throw new Error('Please upload a PDB file');
        }
        const fileContent = await readFileAsText(pdbFile);
        result = countAtomsFromPDB(fileContent);
      }

      setAtomCount(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCalculating(false);
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const resetForm = () => {
    setProteinSequence('');
    setPdbFile(null);
    setAtomCount(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="app">
      <div className="container">
        <header className="header">
          <div className="header-content">
            <Atom className="header-icon" />
            <h1>Protein Atom Counter</h1>
            <p>Calculate the number of atoms in protein structures</p>
          </div>
        </header>

        <main className="main-content">
          <div className="input-section">
            <div className="input-type-selector">
              <button
                className={`input-type-btn ${inputType === 'sequence' ? 'active' : ''}`}
                onClick={() => setInputType('sequence')}
              >
                <FileText size={20} />
                Protein Sequence
              </button>
              <button
                className={`input-type-btn ${inputType === 'pdb' ? 'active' : ''}`}
                onClick={() => setInputType('pdb')}
              >
                <Upload size={20} />
                PDB File
              </button>
            </div>

            {inputType === 'sequence' ? (
              <div className="input-group">
                <label htmlFor="sequence-input">
                  Enter protein sequence (single-letter amino acid codes):
                </label>
                <textarea
                  id="sequence-input"
                  value={proteinSequence}
                  onChange={(e) => setProteinSequence(e.target.value)}
                  placeholder="Example: MKTVRQERLKSIVRILERSKEPVSGAQLAEELSVSRQVIVQDIAYLRSLGYNIVATPRGYVLAGG"
                  rows={6}
                />
                <div className="sequence-info">
                  <Info size={16} />
                  <span>Use standard amino acid codes: A, C, D, E, F, G, H, I, K, L, M, N, P, Q, R, S, T, V, W, Y</span>
                </div>
              </div>
            ) : (
              <div className="input-group">
                <label htmlFor="file-input">
                  Upload PDB structure file:
                </label>
                <div className="file-upload-area">
                  <input
                    ref={fileInputRef}
                    id="file-input"
                    type="file"
                    accept=".pdb"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    className="file-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={24} />
                    {pdbFile ? pdbFile.name : 'Choose PDB File'}
                  </button>
                </div>
              </div>
            )}

            <div className="action-buttons">
              <button
                className="calculate-btn"
                onClick={calculateAtoms}
                disabled={isCalculating || (inputType === 'sequence' ? !proteinSequence.trim() : !pdbFile)}
              >
                <Calculator size={20} />
                {isCalculating ? 'Calculating...' : 'Calculate Atoms'}
              </button>
              <button className="reset-btn" onClick={resetForm}>
                Reset
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {atomCount && (
            <div className="results-section">
              <h2>Results</h2>
              <div className="results-grid">
                <div className="result-card primary">
                  <div className="result-value">{atomCount.totalAtoms.toLocaleString()}</div>
                  <div className="result-label">Total Atoms</div>
                </div>
                <div className="result-card">
                  <div className="result-value">{atomCount.residueCount.toLocaleString()}</div>
                  <div className="result-label">Residues</div>
                </div>
                <div className="result-card">
                  <div className="result-value">
                    {Math.round(atomCount.totalAtoms / atomCount.residueCount)}
                  </div>
                  <div className="result-label">Avg Atoms/Residue</div>
                </div>
              </div>

              {atomCount.breakdown && (
                <div className="breakdown-section">
                  <h3>Amino Acid Composition</h3>
                  <div className="breakdown-grid">
                    {Object.entries(atomCount.breakdown)
                      .sort(([,a], [,b]) => b - a)
                      .map(([aa, count]) => (
                        <div key={aa} className="breakdown-item">
                          <span className="aa-code">{aa}</span>
                          <span className="aa-count">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
